/** The config JSONs the server inlines into the shell, written by server/plugins/boot-payload.ts. */
export const BOOT_PAYLOAD_ELEMENT_ID = '__BOOT_PAYLOAD__'

export interface BootPayload {
  /** Keyed by the path after /api/v2/configs/, e.g. "siteSettings/control.json". */
  configs?: Record<string, unknown>
}

const CONFIG_URL = /\/api\/v2\/configs\/(.+)$/

const asRecord = (value: unknown): Record<string, unknown> | null => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null
  }
  return Object.fromEntries(Object.entries(value))
}

let cached: Record<string, unknown> | null | undefined

const readConfigs = (): Record<string, unknown> | null => {
  if (cached !== undefined) {
    return cached
  }
  cached = null
  if (typeof document === 'undefined') {
    return cached
  }
  const element = document.getElementById(BOOT_PAYLOAD_ELEMENT_ID)
  if (!element?.textContent) {
    return cached
  }
  try {
    const payload = asRecord(JSON.parse(element.textContent))
    cached = payload ? asRecord(payload.configs) : null
  } catch {
    // A malformed payload falls back to the network.
    cached = null
  }
  return cached
}

/**
 * The config the shell carries for a config URL, or undefined when it carries
 * no payload or not that file (a split test variant, for example).
 */
export function readInlineConfig(url: string): unknown {
  const configs = readConfigs()
  if (!configs) {
    return undefined
  }
  const match = CONFIG_URL.exec(url)
  return match ? configs[match[1]] : undefined
}

/** Test hook: forget the parsed payload so the next read hits the document again. */
export function resetBootPayloadCache(): void {
  cached = undefined
}
